import { expectRecord } from './answerShape';
import type { IWorkAreaCloneAddress } from '@featit/shared/clone-address';
import type { IUpdateWorkAreaInput } from '@/data-sources/WorkAreaDataSourceRegistry';
import type {
  IProject,
  IPublicStatus,
  DocumentPlaceTarget,
  IDocumentPlaceListing,
  IWorkAreaDocument,
  IWorkAreaDocumentCheckpoint,
  IWorkAreaDocumentSnapshot,
  IWorkAreaDocumentSummary,
  IDashboardBlock,
  IPagePreview,
  IPageScopeChoices,
  IPageScopeItem,
  IPageShares,
  IWorkAreaDashboardPage,
  IWorkAreaDashboardPageCheckpoint,
  IWorkAreaDashboardPageSnapshot,
  IWorkAreaResource,
  IWorkAreaResourceCheckpoint,
  IWorkAreaResourceSnapshot,
  IWorkAreaDeletionPreview,
  IWorkAreaType,
  IWorkAreaTypeDefaults,
  WorkAreaResourceKindType,
} from '@featit/shared';
import { ApiService, type ApiServiceTransport } from './ApiService';
import { expectList } from './answerShape';
import { workAreaApis } from '@/data-sources/WorkAreaApiRegistry';

export class ProjectApiService extends ApiService {
  findAll(): Promise<IProject[]> {
    return this.get('/work-areas');
  }

  /** F-0338 slice E: what the visibility control says before a work area is made Public (AC-PV22). */
  async publicStatus(id: string, pendingPublishByDefault?: boolean): Promise<IPublicStatus> {
    // Checked at the read (T-0556 SR-13): an answer that is not a status is a failed read with its Try
    // again, not "Public is not available" drawn from fields that are not there.
    return expectRecord<IPublicStatus>(
      await this.get(
        `/work-areas/${id}/public-status${
          pendingPublishByDefault === undefined
            ? ''
            : `?publishByDefault=${pendingPublishByDefault}`
        }`,
      ),
      'The Public status',
      ['visibility', 'counts', 'deploymentAllows'],
    );
  }

  /** F-0338 AC-PV1: allow (or stop allowing) public work areas in an organisation or group. */
  setNamespacePubliclyReadable(
    namespaceId: string,
    publiclyReadable: boolean,
  ): Promise<{ id: string; publiclyReadable: boolean }> {
    return this.put(`/work-areas/public-namespaces/${namespaceId}`, { publiclyReadable });
  }

  findById(id: string): Promise<IProject> {
    return this.get(`/work-areas/${id}`);
  }

  create(data: {
    name: string;
    slug: string;
    description?: string;
    parentId?: string | null;
    visibility?: string;
    workAreaTypeId?: string;
    namespaceId?: string | null;
  }): Promise<IProject> {
    return this.post('/work-areas', data);
  }

  /** T-0502 SZ-9: how many items here hold an estimate, for the scale setting's consequence line. */
  async estimatedItemCount(id: string): Promise<number> {
    return expectRecord<{ estimatedItemCount: number }>(
      await this.get(`/work-areas/${id}/estimated-item-count`),
      'The estimated item count',
      ['estimatedItemCount'],
    ).estimatedItemCount;
  }

  /** The server refuses any field outside `UPDATABLE_WORK_AREA_FIELDS` (I-0751). */
  update(id: string, data: IUpdateWorkAreaInput): Promise<IProject> {
    return this.patch(`/work-areas/${id}`, data);
  }

  /**
   * Make an agent active or inactive in a work area, and give it its role there or take it away
   * (I-0803): the endpoint `featit work-area-agents activate|deactivate` calls.
   */
  setAgentActivation(
    id: string,
    agentId: string,
    active: boolean,
    expectedActive?: boolean,
  ): Promise<IProject> {
    return this.patch(`/work-areas/${id}/agents/${agentId}/activation`, {
      active,
      ...(expectedActive === undefined ? {} : { expectedActive }),
    });
  }

  /**
   * F-0783 (§4d): delete the work area. `confirm` is its name as typed, which the server checks
   * again; a refusal carries its `reason` (`workAreaDeletionRefusal.ts`).
   */
  remove(id: string, confirm?: string): Promise<void> {
    return this.delete(
      `/work-areas/${id}`,
      confirm === undefined ? undefined : { params: { confirm } },
    );
  }

  /** F-0783 (§4d): what deleting the work area removes — the owner's read, refused to anybody else. */
  async deletionPreview(id: string): Promise<IWorkAreaDeletionPreview> {
    return expectRecord<IWorkAreaDeletionPreview>(
      await this.get(`/work-areas/${id}/deletion`),
      'What deleting the work area removes',
      ['id', 'name', 'counts'],
    );
  }

  async workAreaTypes(): Promise<IWorkAreaType[]> {
    // A list or a failed read (I-1162): the store's list is read by every screen that names a type.
    return expectList<IWorkAreaType>(await this.get('/work-areas/types'), 'Work-area types');
  }

  createWorkAreaType(data: {
    key: string;
    name: string;
    titleI18n?: Record<string, string>;
    defaults?: IWorkAreaTypeDefaults;
    position?: number;
    isActive?: boolean;
    isDefault?: boolean;
  }): Promise<IWorkAreaType> {
    return this.post('/work-areas/types', data);
  }

  updateWorkAreaType(
    id: string,
    data: Partial<{
      key: string;
      name: string;
      titleI18n: Record<string, string>;
      defaults: IWorkAreaTypeDefaults;
      position: number;
      isActive: boolean;
      isDefault: boolean;
    }>,
  ): Promise<IWorkAreaType> {
    return this.patch(`/work-areas/types/${id}`, data);
  }

  removeWorkAreaType(id: string): Promise<void> {
    return this.delete(`/work-areas/types/${id}`);
  }

  /** F-0717 (Q11): the work area's clone address, through its own door. */
  cloneAddress(id: string): Promise<IWorkAreaCloneAddress> {
    return this.get(`/work-areas/${id}/clone-address`);
  }

  saveCloneAddress(id: string, cloneUrl: string | null): Promise<IWorkAreaCloneAddress> {
    return this.put(`/work-areas/${id}/clone-address`, { cloneUrl });
  }

  workAreaResources(id: string): Promise<IWorkAreaResourceSnapshot> {
    return this.get(`/work-areas/${id}/resources`);
  }

  workAreaResourceCheckpoint(id: string): Promise<IWorkAreaResourceCheckpoint> {
    return this.get(`/work-areas/${id}/resources/checkpoint`);
  }

  createWorkAreaResource(
    id: string,
    data: {
      title: string;
      url: string;
      description?: string;
      kind?: WorkAreaResourceKindType;
      pinned?: boolean;
      position?: number;
    },
  ): Promise<IWorkAreaResource> {
    return this.post(`/work-areas/${id}/resources`, data);
  }

  updateWorkAreaResource(
    id: string,
    resourceId: string,
    data: Partial<{
      title: string;
      url: string;
      description: string | null;
      kind: WorkAreaResourceKindType;
      pinned: boolean;
      position: number;
    }>,
  ): Promise<IWorkAreaResource> {
    return this.patch(`/work-areas/${id}/resources/${resourceId}`, data);
  }

  removeWorkAreaResource(id: string, resourceId: string): Promise<void> {
    return this.delete(`/work-areas/${id}/resources/${resourceId}`);
  }

  workAreaDocuments(id: string): Promise<IWorkAreaDocumentSnapshot> {
    return this.get(`/work-areas/${id}/documents`);
  }

  /**
   * I-0726: the newest documents across work areas, in one request. Without `workAreaIds`, every
   * work area the caller may read; with them, those of them it may read.
   */
  recentWorkAreaDocuments(
    workAreaIds: readonly string[] | null,
    limit: number,
  ): Promise<{ items: IWorkAreaDocumentSummary[] }> {
    return this.get('/work-areas/documents/recent', {
      params: { limit, ...(workAreaIds ? { workAreaIds: workAreaIds.join(',') } : {}) },
    });
  }

  workAreaDocumentCheckpoint(id: string): Promise<IWorkAreaDocumentCheckpoint> {
    return this.get(`/work-areas/${id}/documents/checkpoint`);
  }

  workAreaDocument(id: string, documentId: string): Promise<IWorkAreaDocument> {
    return this.get(`/work-areas/${id}/documents/${documentId}`);
  }

  /** A new document on one place; naming none puts it on Home (ADR-098). */
  createWorkAreaDocument(
    id: string,
    data: {
      title: string;
      content?: string;
    } & Partial<DocumentPlaceTarget>,
  ): Promise<IWorkAreaDocument> {
    return this.post(`/work-areas/${id}/documents`, data);
  }

  updateWorkAreaDocument(
    id: string,
    documentId: string,
    data: Partial<{
      title: string;
      content: string;
    }>,
  ): Promise<IWorkAreaDocument> {
    return this.patch(`/work-areas/${id}/documents/${documentId}`, data);
  }

  removeWorkAreaDocument(id: string, documentId: string): Promise<void> {
    return this.delete(`/work-areas/${id}/documents/${documentId}`);
  }

  /** The same document on one more page or item — never a copy. */
  attachWorkAreaDocument(
    id: string,
    documentId: string,
    target: DocumentPlaceTarget,
  ): Promise<IWorkAreaDocument> {
    return this.post(`/work-areas/${id}/documents/${documentId}/places`, target);
  }

  /** Refused with 409 for the document's last place. */
  detachWorkAreaDocument(
    id: string,
    documentId: string,
    placeId: string,
  ): Promise<IWorkAreaDocument> {
    return this.delete(`/work-areas/${id}/documents/${documentId}/places/${placeId}`);
  }

  dashboardPageDocuments(id: string, pageId: string): Promise<IDocumentPlaceListing> {
    return this.get(`/work-areas/${id}/dashboard-pages/${pageId}/documents`);
  }

  workItemDocuments(workItemId: string): Promise<IDocumentPlaceListing> {
    return this.get(`/work-items/${workItemId}/documents`);
  }

  /** The documents on a part (F-0320, concept-parts §3.3). */
  partDocuments(partId: string): Promise<IDocumentPlaceListing> {
    return this.get(`/parts/${partId}/documents`);
  }

  workAreaDashboardPages(id: string): Promise<IWorkAreaDashboardPageSnapshot> {
    return this.get(`/work-areas/${id}/dashboard-pages`);
  }

  /** AGM-17: the saved page as a role's people see it; only a manager of the pages may ask. */
  previewWorkAreaDashboardPage(id: string, pageId: string, roleKey: string): Promise<IPagePreview> {
    return this.get(
      `/work-areas/${id}/dashboard-pages/${pageId}/preview?role=${encodeURIComponent(roleKey)}`,
    );
  }

  /** AGM-17: the editor's unsaved draft as a role's people see it; written nowhere. */
  previewWorkAreaDashboardPageDraft(
    id: string,
    pageId: string,
    roleKey: string,
    draft: { blocks: IDashboardBlock[]; audiencePermission: string | null; roleKeys: string[] },
  ): Promise<IPagePreview> {
    return this.post(`/work-areas/${id}/dashboard-pages/${pageId}/preview`, {
      role: roleKey,
      ...draft,
    });
  }

  /**
   * AGM-42: the item an item-scoped page is opened for, read as the reader. A 404 for every item
   * the reader is not shown, whatever the reason (AGM-26).
   */
  workAreaDashboardPageItem(id: string, pageId: string, itemKey: string): Promise<IPageScopeItem> {
    return this.get(
      `/work-areas/${id}/dashboard-pages/${pageId}/items/${encodeURIComponent(itemKey)}`,
    );
  }

  /** PX-60 (I-2426): the items an item-scoped page opened without one offers the reader. */
  workAreaDashboardPageItems(id: string, pageId: string): Promise<IPageScopeChoices> {
    return this.get(`/work-areas/${id}/dashboard-pages/${pageId}/items`);
  }

  workAreaDashboardPageCheckpoint(id: string): Promise<IWorkAreaDashboardPageCheckpoint> {
    return this.get(`/work-areas/${id}/dashboard-pages/checkpoint`);
  }

  createWorkAreaDashboardPage(
    id: string,
    data: {
      title: string;
      /** The page it lives under; absent or null is the top level (F-0201). */
      parentPageId?: string | null;
      position?: number;
      icon?: string | null;
      /** The permission that shows the page; absent, null or empty is everyone (T-0783). */
      audiencePermission?: string | null;
      blocks?: IDashboardBlock[];
    },
  ): Promise<IWorkAreaDashboardPage> {
    return this.post(`/work-areas/${id}/dashboard-pages`, data);
  }

  updateWorkAreaDashboardPage(
    id: string,
    pageId: string,
    data: {
      expectedRevision: number;
      title?: string;
      position?: number;
      icon?: string | null;
      audiencePermission?: string | null;
      /** The item type the page is opened for one item of; null makes it a work-area page (AGM-42). */
      scopeItemTypeKey?: string | null;
      blocks?: IDashboardBlock[];
    },
  ): Promise<IWorkAreaDashboardPage> {
    return this.patch(`/work-areas/${id}/dashboard-pages/${pageId}`, data);
  }

  /**
   * One level's pages, first to last — `parentPageId` names the level, null being the top (F-0201).
   * The server refuses (409) a list that is no longer that level's current set.
   */
  /** Shares a page with exactly these users and roles (T-0783, D-0350). */
  setWorkAreaDashboardPageShares(
    id: string,
    pageId: string,
    shares: IPageShares,
  ): Promise<IPageShares> {
    return this.put(`/work-areas/${id}/dashboard-pages/${pageId}/shares`, shares);
  }

  reorderWorkAreaDashboardPages(
    id: string,
    pageIds: string[],
    parentPageId: string | null = null,
  ): Promise<IWorkAreaDashboardPage[]> {
    return this.put(`/work-areas/${id}/dashboard-pages/order`, { parentPageId, pageIds });
  }

  /** Which page a page lives under, and where among that parent's children (F-0201). */
  moveWorkAreaDashboardPage(
    id: string,
    pageId: string,
    data: { parentPageId: string | null; position?: number },
  ): Promise<IWorkAreaDashboardPage> {
    return this.put(`/work-areas/${id}/dashboard-pages/${pageId}/parent`, data);
  }

  removeWorkAreaDashboardPage(id: string, pageId: string): Promise<void> {
    return this.delete(`/work-areas/${id}/dashboard-pages/${pageId}`);
  }
}

export const projectApi = new ProjectApiService();

/** The work-area API for the connection that holds `workAreaId` (I-1120). */
export function projectApiFor(workAreaId: string | null | undefined): ProjectApiService {
  return workAreaApis.api(
    workAreaId,
    'projectApi',
    (clients) => (clients.transport ? createProjectApi(clients.transport) : projectApi),
    projectApi,
  );
}

export function createProjectApi(transport: ApiServiceTransport): ProjectApiService {
  return new ProjectApiService(transport);
}
